import {
  CheckCircle2,
  ChevronDown,
  CircleMinus,
  CircleX,
  FileCheck,
  Loader2,
  Play,
  Radar,
  TriangleAlert,
} from "lucide-react"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import { api } from "@/lib/api"
import type { AgentRun, AgentStatus, AgentStep } from "@/lib/types"
import { cn } from "@/lib/utils"

/**
 * What the daily job agent did, as it recorded it.
 *
 * Every step is shown with its outcome - done, skipped or failed - and the
 * reason in words, so a quiet morning reads as "nothing new on the boards"
 * rather than as nothing at all. The page draws the record; it does not
 * summarise it into something more cheerful than what happened.
 */

export type AgentActivity = "running" | "ran" | "idle" | "off"

/** Remembered per page, so folding it on Jobs leaves Home as it was. */
const FOLD_KEY = "praxa-agent-report-open:"

function readOpen(page: string, fallback: boolean): boolean {
  try {
    const stored = window.localStorage.getItem(FOLD_KEY + page)
    return stored === null ? fallback : stored === "1"
  } catch {
    return fallback
  }
}

function writeOpen(page: string, open: boolean): void {
  try {
    window.localStorage.setItem(FOLD_KEY + page, open ? "1" : "0")
  } catch {
    // Private windows can refuse storage; the card still folds for this visit.
  }
}

const STEP_ICON = {
  done: <CheckCircle2 className="size-4 text-success" aria-label="done" />,
  skipped: <CircleMinus className="size-4 text-muted-foreground" aria-label="skipped" />,
  failed: <CircleX className="size-4 text-destructive" aria-label="failed" />,
} as const

function when(run: AgentRun): string {
  const started = new Date(run.startedAt)
  const time = started.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  const today = new Date().toISOString().slice(0, 10)
  const day =
    run.day === today
      ? "Today"
      : started.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" })
  const who = run.trigger === "schedule" ? "on schedule" : "you started it"
  return `${day}, ${time} · ${who}`
}

function activityOf(status: AgentStatus | null, running: boolean): AgentActivity {
  if (running) return "running"
  if (!status) return "idle"
  if (!status.enabled) return "off"
  return status.today ? "ran" : "idle"
}

function Step({ step }: { step: AgentStep }) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 shrink-0">{STEP_ICON[step.status]}</span>
      <div className="min-w-0">
        <p className="text-sm font-medium">
          {step.label}
          {step.seconds >= 1 ? (
            <span className="ml-2 font-mono text-[11px] font-normal text-muted-foreground">
              {step.seconds.toFixed(0)} s
            </span>
          ) : null}
        </p>
        <p className="text-sm text-muted-foreground">{step.detail}</p>
      </div>
    </li>
  )
}

export function AgentReport({
  onActivity,
  onFinished,
  page = "home",
  defaultOpen = true,
}: {
  onActivity?: (activity: AgentActivity) => void
  /** After a run started here finishes - the Jobs page reloads its list. */
  onFinished?: () => void
  /** Which page shows it - each remembers its own folded state. */
  page?: string
  /** Whether the report starts unfolded the first time this page shows it. */
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(() => readOpen(page, defaultOpen))
  const [status, setStatus] = useState<AgentStatus | null>(null)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api.agent
      .status()
      .then((loaded) => !cancelled && setStatus(loaded))
      .catch((caught: Error) => !cancelled && setError(caught.message))
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    onActivity?.(activityOf(status, running))
  }, [status, running, onActivity])

  // No try/finally, and nothing conditional inside the try: the React
  // Compiler skips a component that has either.
  async function runNow() {
    setRunning(true)
    setError(null)
    let finished = false
    try {
      await api.agent.run()
      setStatus(await api.agent.status())
      finished = true
    } catch (caught) {
      setError((caught as Error).message)
    }
    setRunning(false)
    if (finished && onFinished) onFinished()
  }

  async function toggle() {
    if (!status) return
    try {
      setStatus(await api.agent.setEnabled(!status.enabled))
    } catch (caught) {
      setError((caught as Error).message)
    }
  }

  const run = status?.today ?? status?.latest ?? null
  const strong = run ? run.matches.filter((match) => match.score >= 0.7).length : 0

  function fold() {
    setOpen(!open)
    writeOpen(page, !open)
  }

  return (
    <section
      aria-labelledby="agent-report-title"
      className="rounded-2xl border border-border/70 bg-card/60 p-5 backdrop-blur"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
            <Radar className="size-3.5" aria-hidden /> Daily job agent
          </p>
          <h2 id="agent-report-title" className="mt-1 text-lg font-semibold">
            Every morning: new postings, scored against your CV
          </h2>
        </div>
        {status ? (
          <button
            type="button"
            role="switch"
            aria-checked={status.enabled}
            onClick={() => void toggle()}
            className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span
              aria-hidden
              className={cn(
                "relative h-4 w-7 rounded-full transition-colors",
                status.enabled ? "bg-primary" : "bg-muted-foreground/30",
              )}
            >
              <span
                className={cn(
                  "absolute top-0.5 size-3 rounded-full bg-background transition-all",
                  status.enabled ? "left-3.5" : "left-0.5",
                )}
              />
            </span>
            {status.enabled ? "On" : "Off"}
          </button>
        ) : null}
      </div>

      <button
        type="button"
        onClick={fold}
        aria-expanded={open}
        aria-controls="agent-report-body"
        className="mt-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
      >
        <ChevronDown
          className={cn("size-3.5 transition-transform", open ? "rotate-180" : "")}
          aria-hidden
        />
        {open
          ? "Hide the report"
          : run
            ? `Show the report · ${when(run)} · ${strong} strong ${strong === 1 ? "match" : "matches"}`
            : "Show the report"}
      </button>

      <div id="agent-report-body" hidden={!open}>

      {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}

      {running ? (
        <p className="mt-4 flex items-center gap-2 text-sm" role="status">
          <Loader2 className="size-4 animate-spin text-primary" aria-hidden />
          Running: fetching from the job boards and scoring the closest postings. Usually under a
          minute.
        </p>
      ) : run ? (
        <div className="mt-4 space-y-4">
          <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>{when(run)}</span>
            {status?.today ? null : <Badge variant="outline">not run yet today</Badge>}
          </p>
          <ol className="space-y-3" aria-label="What the agent did">
            {run.steps.map((step) => (
              <Step key={step.key} step={step} />
            ))}
          </ol>

          {run.matches.length > 0 ? (
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Best matches
              </p>
              <ul className="space-y-2">
                {run.matches.map((match) => (
                  <li
                    key={match.jobId}
                    className="flex flex-wrap items-start gap-3 rounded-xl border bg-background/40 p-3"
                  >
                    <span
                      className={cn(
                        "rounded-md px-2 py-0.5 font-mono text-xs font-semibold",
                        match.score >= 0.7
                          ? "bg-success/15 text-success"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      {Math.round(match.score * 100)}%
                    </span>
                    {/* A basis wide enough for a title: on a phone the button
                        wraps under the text instead of squeezing it. */}
                    <div className="min-w-0 flex-1 basis-56">
                      <p className="text-sm font-medium">
                        {match.title} <span className="text-muted-foreground">· {match.company}</span>
                      </p>
                      {match.verdict ? (
                        <p className="line-clamp-2 text-xs text-muted-foreground">{match.verdict}</p>
                      ) : null}
                      {(match.flags ?? []).map((flag) => (
                        <p key={flag} className="mt-1 flex items-start gap-1 text-xs text-warning">
                          <TriangleAlert className="mt-0.5 size-3 shrink-0" aria-hidden />
                          <span>Against your priorities – {flag}</span>
                        </p>
                      ))}
                    </div>
                    <Link
                      to={`/documents?job=${encodeURIComponent(match.jobId)}`}
                      className={cn(buttonVariants({ size: "sm", variant: "outline" }))}
                    >
                      <FileCheck aria-hidden /> Write an application
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : status ? (
        <p className="mt-4 text-sm text-muted-foreground">
          No run yet. The first one happens tomorrow morning, or start it now.
        </p>
      ) : null}

      {status ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <p className="max-w-xl text-xs text-muted-foreground">
            Up to {status.dailyScores} postings a day, from the agent&apos;s own budget - your own
            daily calls are untouched. It needs a CV for at least one target role.
          </p>
          {status.canRunNow && status.enabled ? (
            <Button size="sm" disabled={running} onClick={() => void runNow()}>
              <Play aria-hidden /> Run now
            </Button>
          ) : null}
        </div>
      ) : null}
      </div>
    </section>
  )
}
